import React, { useEffect } from 'react';
import { PaletteProvider } from '@binaa-almajal/ui-kit/providers/PaletteProvider';
import { I18nProvider } from '@binaa-almajal/ui-kit/providers/I18nProvider';
import { AuthProvider } from '@binaa-almajal/ui-kit/providers/AuthProvider';
import { AlertProvider } from '@binaa-almajal/ui-kit/providers/AlertProvider';
import { HeadProvider } from '@binaa-almajal/ui-kit/providers/HeadProvider';
import { useI18n } from '@binaa-almajal/ui-kit/hooks/useI18n';
import { loadCurrencies } from '../config/currency';

// The provider stack, in the one order that works. Composed here so App.jsx
// stays a single line and the ordering constraints live next to each other:
//
//   PaletteProvider outermost, and it could sit anywhere: it reads nothing from
//                  the others and only writes one <style> element. It is first
//                  because the colours it installs are what everything below
//                  renders in.
//   I18nProvider   outermost of the four — AlertProvider calls useI18n to
//                  translate error codes.
//   AuthProvider   before AlertProvider only by convention (alerts do not read
//                  auth today).
//   AlertProvider — leafmost; nothing below depends on it.
//
// The router is NOT here: AppRoutes owns it, and the error boundary sits
// OUTSIDE all of this in App.jsx, because a boundary inside the providers
// cannot catch a provider throwing.

// THE SEED IS NOW A HUE AND NOTHING ELSE, and both halves of that are the
// point.
//
// `hue: 266` is the brand's own — the hue of the mark, and the hue ui-kit
// generates its default ramps at. It replaces 190. That teal was chosen while
// PaletteProvider was prepending its stylesheet under the preset's `:root`
// defaults, so it never reached a pixel and nobody ever saw it; 5.0.0 fixes
// that, which makes this the first release where the number here is visible at
// all. 266 is also the hue with the most colour available where this
// application paints most: sRGB gives a blue 0.232 chroma at the lightness of
// `primary-600` — the button fill, the icon ink, the link — and a cyan 0.086.
//
// `chroma: 1.6` AND THE `neutralChroma` PAIR ARE GONE, not lost. In 4.2.1 the
// multiplier scaled an ABSOLUTE chroma that the gamut map then clipped, so 1.6
// was compensating for a ramp that was under-saturated by construction and was
// itself returning as little as 57% of what it asked for. In 5.0.0 a rung is a
// fraction of what the display can actually supply, and the profile is already
// tuned; 1.6 on top of it drives the light rungs to the edge of sRGB —
// `primary-200` comes out #00f7ee, a neon — and turns the neutral visibly
// cyan. The kit's own values are the intended ones, so this passes none.
// 5.4.0: 250 RATHER THAN 266, AND A DE-TINTED DARK NEUTRAL.
//
// `hue` is a hue and nothing else, and 266 is where blue has already started
// turning into indigo: `primary-600` — the button fill, the icon ink, the link,
// and therefore most of the brand anybody actually sees — came out #3f66d0,
// which reads violet beside the mark. 250 puts it at #1a71bc. sRGB gives 250 a
// little less chroma at that lightness than 266 has, which is the price and it
// is small; the generator caps both at the same ceiling anyway, so the
// difference on screen is hue, not loudness.
//
// `dark: { neutralChroma: 0.25 }` IS THE OTHER HALF AND IT IS NOT ABOUT BLUE.
// Every rung of `gray` carries the brand hue on purpose — a page of untinted
// grey cards reads as a photocopy, which is what the full-strength tint fixed
// at the light end. At the DARK end the same tint is what made the whole
// scheme navy: `gray-900` was #0b1b2a, a colour, and it was the paper. A
// quarter of the tint leaves #171a1e — a near-black that is still not a
// machine grey. LIGHT IS LEFT AT FULL STRENGTH, because the finding that put
// the tint there in the first place was a finding about white surfaces.
//
// The two halves are separable: the hue is the brand, the neutral is the
// paper, and ui-kit's 5.4.0 dark surface ladder (canvas at `gray-950` rather
// than `gray-900`, a neutral sidebar) is what makes the second one visible.
const PALETTE = { hue: 250, dark: { neutralChroma: 0.25 } };

/**
 * The site-wide half of every public page's <head> (the page adds its own
 * with useDocumentHead): the origin its canonical addresses are built on, and
 * the name its titles end in, in the page's language. The origin is this
 * page's own — the same bundle serves staging and production, and each is its
 * own canonical. The prerender (src/prerender/publicEntry.jsx) supplies the
 * deployed origin instead, since a build has no window.
 */
export function AcademyHead({ children }) {
    const { t } = useI18n();
    const origin = typeof window === 'undefined' ? undefined : window.location.origin;
    return <HeadProvider origin={origin} siteName={t('app.name')} image="/og-image.png">{children}</HeadProvider>;
}

/** Asks the server which currencies the header may offer, once, at start-up. */
function CurrencyBootstrap() {
    useEffect(() => { void loadCurrencies(); }, []);
    return null;
}

export const AppProviders = ({ children }) => (
    <PaletteProvider seed={PALETTE}>
        <I18nProvider localeInUrl>
            <AcademyHead>
                <AuthProvider>
                    <AlertProvider>
                        <CurrencyBootstrap />
                        {children}
                    </AlertProvider>
                </AuthProvider>
            </AcademyHead>
        </I18nProvider>
    </PaletteProvider>
);
